import { BuildClass, Do, fsmData } from '../../../universal';
import { React, _ } from '../../lib';
import { getInternalValue, getValuesAfterClick, resolvePendingDeltas, } from './list-util';
import { ConditionalObject, useMemoCompare, useRSInstance, useStateSync, } from './meta-types';
import { stubDiv, stubInput } from './stubs';
const SCROLL_BUFFER = 100;
const SCROLL_JACK_GAP = 10;
const PAGE_JUMP = 12;
const HOME_END_JUMP = 10e7;
var Action;
(function (Action) {
    Action[Action["NoOp"] = 0] = "NoOp";
    Action[Action["UpdateSelectedValues"] = 1] = "UpdateSelectedValues";
    Action[Action["OnKeyPress"] = 2] = "OnKeyPress";
    Action[Action["OnMouseDown"] = 3] = "OnMouseDown";
    Action[Action["OnClick"] = 4] = "OnClick";
    Action[Action["OnScroll"] = 5] = "OnScroll";
    Action[Action["UpdateViewportHeight"] = 6] = "UpdateViewportHeight";
})(Action || (Action = {}));
const getPartialStateFromAction = (rs, p) => {
    switch (p[0]) {
        case Action.NoOp:
            return {};
        case Action.UpdateSelectedValues:
            return setSelected(rs, p[1].value);
        case Action.OnKeyPress:
            return onKeyDownList(rs, p[1].ts, p[1].evWhich, p[1].evShiftKey);
        case Action.OnMouseDown:
            return onMouseDownList(rs, p[1].ev);
        case Action.OnClick:
            return onMouseClickList(rs, p[1].ev);
        case Action.OnScroll: {
            const div = rs.refs.wrappingElement.current;
            return { scrollOffset: div.scrollTop };
        }
        case Action.UpdateViewportHeight:
            return { vpHeight: p[1].height };
    }
};
const ListComponent = (props, ref) => {
    const rs = useRSInstance({
        props: props,
        defaultState: React.useCallback(props => ({
            value: getInternalValue(props.value ?? []),
            scrollOffset: 0,
            vpHeight: props.vpHeight ?? null,
            mouseDown: false,
        }), []),
        refs: {
            hasMounted: React.useRef(false),
            hiddenInput: React.useRef(stubInput),
            wrappingElement: React.useRef(stubDiv),
            pendingDeltas: React.useRef([]),
            childPosHeights: React.useRef(new Map()),
            handledEvents: React.useRef(new Set()),
            dispatchRef: React.useRef(() => {
                console.error('Dispatch called before dispatchRef set');
            }),
        },
        actionToDelta: getPartialStateFromAction,
    });
    rs.refs.dispatchRef.current = rs.dispatch;
    useStateSync({
        stateVal: rs.state.value,
        propVal: props.value,
        setState: propVal => {
            rs.dispatch([
                Action.UpdateSelectedValues,
                { value: getInternalValue(propVal ?? []) },
            ]);
        },
        setProp: stateVal => {
            const val = props.multiple ? stateVal : _.first(stateVal);
            props.onUpdate(val);
        },
        compareFn: (propVal, stateVal) => {
            const sVal = props.multiple ? stateVal : _.first(stateVal);
            return _.isEqual(propVal, sVal);
        },
    });
    rs.refs.childPosHeights.current = useMemoCompare(() => getChildPosHeights(props.rows ?? [], props.height), { height: props.height, children: props.rows });
    React.useEffect(() => {
        if (props.vpHeight == null) {
            const observer = new ResizeObserver(() => {
                if (rs.refs.hasMounted.current) {
                    rs.dispatch([
                        Action.UpdateViewportHeight,
                        { height: rs.refs.wrappingElement.current?.clientHeight ?? 0 },
                    ]);
                }
            });
            observer.observe(rs.refs.wrappingElement.current);
            return () => {
                observer.disconnect();
            };
        }
        return _.noop;
    }, [rs.refs.wrappingElement.current, rs.refs.hasMounted.current, props.vpHeight]);
    React.useEffect(() => {
        rs.refs.hasMounted.current = true;
        rs.dispatch([Action.NoOp]);
        return () => {
            rs.refs.hasMounted.current = false;
        };
    }, []);
    const evScroll = React.useCallback((ev) => {
        rs.dispatch([Action.OnScroll, { ev }]);
    }, []);
    const evMouseDown = React.useCallback((ev) => {
        rs.dispatch([Action.OnMouseDown, { ev }]);
    }, []);
    const evMouseClick = React.useCallback((ev) => {
        rs.dispatch([Action.OnClick, { ev }]);
    }, []);
    const evKeyDown = React.useCallback((ev) => {
        if (ev.which == 33 ||
            ev.which == 34 ||
            ev.which == 35 ||
            ev.which == 36 ||
            ev.which == 38 ||
            ev.which == 40) {
            ev.stopPropagation();
            ev.preventDefault();
        }
        rs.dispatch([
            Action.OnKeyPress,
            { ts: ev.timeStamp, evWhich: ev.which, evShiftKey: ev.shiftKey },
        ]);
    }, []);
    const evKeyDownWrapped = React.useCallback((ev) => {
        if (props.onKeyDown != null) {
            props.onKeyDown(ev, evKeyDown);
        }
        else {
            evKeyDown(ev);
        }
    }, [props.onKeyDown]);
    const { spacerTop, rows, spacerBottom } = useMemoCompare(() => getRenderedRowsAndSpacerSize(rs.props.rows, rs.state.scrollOffset, rs.state.vpHeight ?? 0, rs.refs.childPosHeights.current, rs.props.disableLazyRendering ?? false), [
        rs.props.rows,
        rs.state.scrollOffset,
        rs.state.vpHeight,
        rs.refs.childPosHeights.current,
    ]);
    React.useImperativeHandle(ref, () => ({
        focus: () => {
            rs.refs.hiddenInput.current?.focus();
        },
        blur: () => {
            rs.refs.hiddenInput.current?.blur();
        },
        selectAll: () => {
            rs.dispatch([
                Action.UpdateSelectedValues,
                { value: getSelectableValues(rs.props.rows) },
            ]);
        },
        selectNone: () => {
            rs.dispatch([Action.UpdateSelectedValues, { value: [] }]);
        },
        setSelected: (selected) => {
            rs.dispatch([Action.UpdateSelectedValues, { value: selected }]);
        },
        moveSelection: (delta, shiftKey = false) => {
            moveSelectionDelta(rs, delta, shiftKey);
        },
        updateScrollPosition: (newSelected) => {
            updateScrollPosition(rs, newSelected);
        },
        refreshScrollJack: () => {
            updateScrollPosition(rs, rs.state.value);
        },
        rs: rs,
        renderedRows: rows,
        getKeyDownEventDelta: (ts, evWhich, evShiftKey) => onKeyDownList(rs, ts, evWhich, evShiftKey).value ?? [],
        forwardEventScroll: evScroll,
        forwardEventMouseDown: evMouseDown,
        forwardEventMouseClick: evMouseClick,
        forwardEventKeyDown: (ts, evWhich, evShiftKey) => {
            rs.dispatch([Action.OnKeyPress, { ts, evWhich, evShiftKey }]);
        },
        noop: () => {
            rs.dispatch([Action.NoOp]);
        },
        getElement: () => rs.refs.wrappingElement.current,
        getRowValues: () => rs.props.rows.map(x => x.value),
    }));
    return (React.createElement("div", { ref: rs.refs.wrappingElement, className: BuildClass({
            'ui5 ui5-list': true,
            [BuildClass(props.className)]: true,
        }), onScroll: evScroll, style: props.style },
        React.createElement("div", { className: "hidden-input" }, ConditionalObject(!(props.disableFocusControl ?? false), () => (React.createElement("input", { readOnly: true, ref: rs.refs.hiddenInput, onKeyDown: evKeyDownWrapped })))),
        React.createElement("div", { className: "spacer-top", style: { height: spacerTop } }),
        rows.map(x => (React.createElement(ListRow, { key: x.key ?? x.value, value: x.value, selected: rs.state.value.includes(x.value), selectable: x.selectable ?? true, className: x.className ?? '', content: x.content, evMouseDown: evMouseDown, evMouseClick: evMouseClick }))),
        React.createElement("div", { className: "spacer-bottom", style: { height: spacerBottom } })));
};
const ListRow = (props) => {
    const content = props.content();
    return (React.createElement("div", { className: BuildClass({
            'ui5-row': true,
            [BuildClass(props.className)]: true,
            selected: props.selected,
            'non-selectable': !props.selectable,
        }), "row-value": props.value, onMouseDown: props.evMouseDown, onMouseUp: props.evMouseClick }, content));
};
const getChildPosHeights = (children, overrideHeight) => {
    let cHeight = 0;
    const posHeight = new Map();
    _.forEach(children, item => {
        const top = cHeight;
        const height = item.height ?? overrideHeight;
        const bot = top + height;
        cHeight += height;
        posHeight.set(item.value, { top, height, bot, item });
    });
    return posHeight;
};
const updateScrollPosition = (rs, newSelected) => {
    const row = rs.refs.childPosHeights.current.get(newSelected[newSelected.length - 1]);
    if (!row) {
        return;
    }
    let newScrollPos = null;
    const vpHeight = rs.state.vpHeight ?? 0;
    if (row.top < rs.state.scrollOffset) {
        newScrollPos = row.top - SCROLL_JACK_GAP;
    }
    else if (row.bot > rs.state.scrollOffset + vpHeight) {
        newScrollPos = row.bot + SCROLL_JACK_GAP - vpHeight;
    }
    if (newScrollPos) {
        rs.refs.wrappingElement.current.scrollTo({
            top: newScrollPos,
        });
    }
};
const setSelected = (rs, value) => {
    if (rs.props.readOnly) {
        return {};
    }
    updateScrollPosition(rs, value);
    return { value };
};
const onMouseDownList = (rs, ev) => {
    if (rs.props.disableClickHandlers || rs.props.readOnly) {
        return {};
    }
    rs.refs.hiddenInput.current?.focus({ preventScroll: true });
    if (ev.button != 0) {
        return {};
    }
    ev.preventDefault();
    ev.stopPropagation();
    const val = Do(() => {
        const div = ev.target.closest('.ui5-row');
        const valStr = div.attributes.getNamedItem('row-value')?.value;
        if (_.isNumber(rs.props.rows[0]?.value)) {
            return +valStr;
        }
        return valStr;
    });
    if (!getSelectableValues(rs.props.rows).includes(val)) {
        return {};
    }
    let modifier;
    if (!rs.props.multiple) {
        modifier = 1;
    }
    else if (ev.shiftKey) {
        modifier = 3;
    }
    else if (ev.ctrlKey || rs.props.multipleDefault) {
        modifier = 2;
    }
    else {
        modifier = 1;
    }
    return {
        value: getValuesAfterClick(getSelectableValues(rs.props.rows), rs.state.value, val, modifier),
    };
};
const onMouseClickList = (rs, ev) => {
    if (rs.props.disableClickHandlers || rs.props.readOnly) {
        return {};
    }
    rs.refs.hiddenInput.current?.focus({ preventScroll: true });
    ev.preventDefault();
    ev.stopPropagation();
    return {};
};
const onKeyDownList = (rs, ts, evWhich, evShiftKey) => {
    if (rs.refs.handledEvents.current.has(ts)) {
        return {};
    }
    rs.refs.handledEvents.current.add(ts);
    const move = (d) => {
        moveSelectionDelta(rs, d, evShiftKey);
    };
    switch (evWhich) {
        case 38:
            move(-1);
            break;
        case 40:
            move(+1);
            break;
        case 33:
            move(-PAGE_JUMP);
            break;
        case 34:
            move(+PAGE_JUMP);
            break;
        case 36:
            move(-HOME_END_JUMP);
            break;
        case 35:
            move(+HOME_END_JUMP);
            break;
    }
    return {};
};
const moveSelectionDelta = (rs, delta, shiftKey) => {
    rs.refs.pendingDeltas.current.push({
        delta: delta,
        shift: shiftKey,
    });
    requestAnimationFrame(() => {
        resolveDeltas(rs);
    });
};
const resolveDeltas = (rs) => {
    if (!rs.refs.hasMounted.current) {
        return;
    }
    const selected_items = resolvePendingDeltas(rs.refs.pendingDeltas.current, rs.state.value, getSelectableValues(rs.props.rows), rs.props.multiple);
    if (selected_items) {
        rs.refs.dispatchRef.current([
            Action.UpdateSelectedValues,
            { value: selected_items },
        ]);
    }
    rs.refs.pendingDeltas.current = [];
};
const getRenderedRowsAndSpacerSize = (children, scrollOffset, vpHeight, childPosHeights, disableLazyRendering) => {
    if (disableLazyRendering) {
        return {
            spacerTop: 0,
            rows: children,
            spacerBottom: 0,
        };
    }
    const rows = [];
    let rowIndexStart = -1;
    const cutoff_top = scrollOffset - SCROLL_BUFFER;
    const cutoff_bot = scrollOffset + vpHeight + SCROLL_BUFFER;
    let spacerTop = 0;
    let spacerBottom = 0;
    let doingSpacerTop = true;
    let doingSpacerBottom = false;
    if (!childPosHeights || !vpHeight) {
        return {
            spacerTop: _.sum(children.map(x => x.height)),
            rows: [],
            spacerBottom: 0,
        };
    }
    _.forEach(children, (row, index) => {
        const ph = childPosHeights.get(row.value);
        if (!ph) {
            console.warn('No pos height found for row', row);
            return;
        }
        if (doingSpacerTop && ph.bot > cutoff_top) {
            doingSpacerTop = false;
        }
        if (!doingSpacerTop && !doingSpacerBottom && ph.top > cutoff_bot) {
            doingSpacerBottom = true;
        }
        if (doingSpacerTop) {
            spacerTop += ph.height;
        }
        else if (doingSpacerBottom) {
            spacerBottom += ph.height;
        }
        else {
            rows.push(row);
            if (rowIndexStart == -1) {
                rowIndexStart = index;
            }
        }
    });
    if (rowIndexStart >= 1 && rowIndexStart % 2 == 1) {
        const insertRow = children[rowIndexStart - 1];
        if (!insertRow) {
            throw new Error('No insert row found');
        }
        const ph = childPosHeights.get(insertRow.value);
        spacerTop -= ph?.height ?? 0;
        rows.unshift(insertRow);
    }
    return { spacerTop, rows, spacerBottom };
};
const getSelectableValues = (rows) => fsmData(rows, {
    filter: x => x.selectable ?? true,
    map: x => x.value,
});
export const List = React.forwardRef(ListComponent);
//# sourceMappingURL=list.js.map